import React from 'react';
import { userLawyerService, userService } from '@/api';
import { errorFieldMessage } from '@/const';
import { useNotification } from './useNotification';
import { FieldValues, useForm } from 'react-hook-form';
import { PersonalDetailsI, UserI } from '@/interfaces';
import { formatPhoneRemove } from '@/lib';

type GoogleLocation = {
	state?: string;
	city?: string;
	zip?: string;
};

// Survives PersonalDetails remounts after save/refetch (same browser session)
const googleLocationByUserId = new Map<string, GoogleLocation>();

const phoneErrorMessage = (role: string | undefined): string => {
	if (role === 'lawyer') {
		return 'Your phone number is required for two-factor authentication (2FA)';
	}
	return errorFieldMessage;
};

interface Props {
	id: string;
	userInformation?: UserI;
	refetch?: () => void;
	requireAddress?: boolean;
}

const NAME_PATTERN = /^[A-Za-zÀ-ÖØ-öø-ÿ]+(?:[ '’-][A-Za-zÀ-ÖØ-öø-ÿ]+)*$/;
const NAME_PATTERN_MESSAGE =
	'Only letters, spaces, hyphens and apostrophes accepted';

export const usePersonalInformation = ({
	id,
	userInformation,
	refetch,
	requireAddress = false,
}: Props) => {
	const phoneNumberErrorMsg = phoneErrorMessage(userInformation?.role);

	const rules = {
		file: {
			required: { value: true, message: errorFieldMessage },
		},
		// CLAIM-3486 — first/last name are no longer required to move forward
		// with onboarding; keep format validation only.
		firstName: {
			pattern: {
				value: NAME_PATTERN,
				message: NAME_PATTERN_MESSAGE,
			},
			maxLength: {
				value: 35,
				message: 'Your first name has to have less than 35 characters',
			},
		},
		lastName: {
			pattern: {
				value: NAME_PATTERN,
				message: NAME_PATTERN_MESSAGE,
			},
			maxLength: {
				value: 35,
				message: 'Your last name has to have less than 35 characters',
			},
		},
		address: requireAddress
			? {
					required: { value: true, message: errorFieldMessage },
					minLength: {
						value: 6,
						message: 'Please enter a valid address',
					},
			  }
			: {
					required: { value: false },
			  },
		city: {
			required: { value: requireAddress, message: errorFieldMessage },
			validate: (value: string, formValues: FieldValues) => {
				if (formValues.address?.trim() && !value) return errorFieldMessage;
				return true;
			},
		},
		state: {
			required: { value: requireAddress, message: errorFieldMessage },
			validate: (value: string, formValues: FieldValues) => {
				if (formValues.address?.trim() && !value) return errorFieldMessage;
				return true;
			},
		},
		zipCode: {
			required: { value: requireAddress, message: errorFieldMessage },
			validate: (value: string, formValues: FieldValues) => {
				if (formValues.address?.trim() && !value) return errorFieldMessage;
				return true;
			},
		},
		phone: {
			required: {
				// value: userInformation?.role === 'lawyer' ? true : false,
				value: true,
				message: phoneNumberErrorMsg,
			},
		},
	};

	const { toastSuccess, toastError } = useNotification();
	const form = useForm({ mode: 'onChange' });
	const { watch, reset, trigger, getValues } = form;

	const [initialPhone, setInitialPhone] = React.useState('');
	const [forceUpdate, setForceUpdate] = React.useState(0);
	const [previousFile, setPreviousFile] = React.useState<string>();
	const [removedPrevFile, setRemovedPrevFile] = React.useState(false);
	const [isOpenModalDelete, setIsOpenModalDelete] = React.useState(false);
	const [addSecondEmail, setAddSecondEmail] = React.useState<boolean>(false); // this state was moved here so it can interact with the save a cancel buttons
	const [initialAdditionalEmail, setInitialAdditionalEmail] = React.useState<
		string | null
	>('');

	const googleLocationKey = String(userInformation?.id || id);
	const googleLocationRef = React.useRef<GoogleLocation>(
		googleLocationByUserId.get(googleLocationKey) || {}
	);
	// Survives form reset after save — used to validate state/city/zip vs Google address
	const [selectedGoogleLocation, setSelectedGoogleLocationState] =
		React.useState<GoogleLocation>(
			() => googleLocationByUserId.get(googleLocationKey) || {}
		);

	const setSelectedGoogleLocation = React.useCallback(
		(location: GoogleLocation) => {
			googleLocationRef.current = location;
			googleLocationByUserId.set(googleLocationKey, location);
			setSelectedGoogleLocationState(location);
		},
		[googleLocationKey]
	);

	const clearSelectedGoogleLocation = React.useCallback(() => {
		googleLocationRef.current = {};
		googleLocationByUserId.delete(googleLocationKey);
		setSelectedGoogleLocationState({});
	}, [googleLocationKey]);

	// Keep ref/state in sync if the user key becomes available after mount
	React.useEffect(() => {
		const stored = googleLocationByUserId.get(googleLocationKey);
		if (stored && Object.keys(stored).length > 0) {
			googleLocationRef.current = stored;
			setSelectedGoogleLocationState(stored);
		}
	}, [googleLocationKey]);

	const additionalEmail = watch('additionalEmail');
	const isVerified = userInformation?.is_verified;
	const hasFirstname = userInformation?.firstname;
	const hasLastname = userInformation?.lastname;

	React.useEffect(() => {
		setInitialData();
	}, [id, userInformation]);

	React.useEffect(() => {
		if (userInformation && (watch('zipCode') || watch('address'))) {
			setTimeout(() => {
				trigger();
			}, 500);
		}
	}, [watch('zipCode'), watch('address'), userInformation]);

	React.useEffect(() => {
		if (additionalEmail) {
			setAddSecondEmail(true);
		}
	}, [additionalEmail]);

	const setInitialData = (): void => {
		if (userInformation) {
			const user = { ...userInformation };
			reset({
				file: user.profile_picture,
				firstName:
					!user.firstname || user.firstname === 'null' ? '' : user.firstname,
				lastName:
					!user.lastname || user.lastname === 'null' ? '' : user.lastname,
				email: user.email,
				additionalEmail: user.additional_email,
				phone: user?.phonenumber || '',
				address: user.address1,
				address2: user.address2 || '',
				zipCode: user.zip_code,
				stateName:
					typeof user.state === 'string' ? user.state : user.state?.name || '',
				cityName:
					typeof user.city === 'string' ? user.city : user.city?.name || '',
			});
			setRemovedPrevFile(false);
			setPreviousFile(user.profile_picture);
			setInitialPhone(user?.phonenumber || '');
			setInitialAdditionalEmail(user.additional_email);
		}
	};

	const savePersonalData = async (
		data: FieldValues,
		silent: boolean
	): Promise<void> => {
		const zipCode = data.zipCode || getValues('zipCode');
		const normalizeZip = (value: string) => value.replace(/\D/g, '');
		const zipsMatch = (a?: string, b?: string) =>
			!!a && !!b && normalizeZip(a) === normalizeZip(b);

		// Always read from ref so validation survives remounts / stale closures
		const googleLocation =
			googleLocationRef.current ||
			googleLocationByUserId.get(googleLocationKey) ||
			{};

		if (
			googleLocation.zip &&
			zipCode &&
			!zipsMatch(googleLocation.zip, zipCode)
		) {
			if (!silent)
				toastError(
					'The selected Zip Code does not match the provided Address.'
				);
			return;
		}

		if (!data.additionalEmail) {
			setAddSecondEmail(false);
		}
		try {
			const userData: PersonalDetailsI = {
				doc: typeof data.file === 'string' ? null : data.file,
				firstName: data.firstName,
				lastName: data.lastName,
				address1: data.address || null,
				address2: data.address2?.trim() ?? '',
				city: (data.cityName || getValues('cityName') || '').trim() || null,
				state: (data.stateName || getValues('stateName') || '').trim() || null,
				zipCode: data.zipCode || null,
				phoneNumber: formatPhoneRemove(data.phone),
				additionalEmail: data.additionalEmail,
				receiveUpdates: userInformation?.receive_updates || false,
				removeProfilePicture: removedPrevFile && !data.file,
			};

			if (userInformation?.role === 'lawyer') {
				const response = await userLawyerService.updatePersonalInformation(
					userData
				);
				if (!silent) toastSuccess(response.data.message);
			} else {
				const response = await userService.updatePersonalInformation(userData);
				if (!silent) toastSuccess(response.data.message);
			}
			setRemovedPrevFile(false);
			refetch?.();
		} catch (error: any) {
			console.error('ERROR', error);
			const message = error?.message?.toLowerCase().includes('phone') // show phone error message exception
				? error?.message
				: 'Something went wrong. Please try again.';
			toastError(message);
		}
	};

	const handleSave = (data: FieldValues) => savePersonalData(data, false);
	const handleSaveSilent = (data: FieldValues) => savePersonalData(data, true);

	return {
		rules,
		isVerified,
		hasFirstname,
		hasLastname,
		handleSave,
		handleSaveSilent,
		setInitialData,
		setAddSecondEmail,
		setForceUpdate,
		removedPrevFile,
		previousFile,
		setRemovedPrevFile,
		addSecondEmail,
		initialAdditionalEmail,
		initialPhone,
		forceUpdate,
		setIsOpenModalDelete,
		isOpenModalDelete,
		form,
		selectedGoogleLocation,
		setSelectedGoogleLocation,
		clearSelectedGoogleLocation,
	};
};
